
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { RoleObject } from '@/store/modules/Members/RolesModule';
  import { useAbility } from '@casl/vue';
  import PermissionsTable from '@/components/roles/PermissionsTable.vue';

  export default defineComponent({
    name: 'roles-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
      PermissionsTable,
    },
    async setup() {
      const { t, te } = useI18n();
      const { can } = useAbility();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const selectedPermissions = ref<Array<number>>([]);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('roleUpdating'), [
          translate('members'),
        ]);
      });

      await store.dispatch(Actions.GET_ROLES_GROUPS);
      const { data } = await store.dispatch(Actions.GET_ROLE, route.params.id);

      const role = ref<RoleObject>(data.data);
      selectedPermissions.value = role.value.rolePermission;

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const roleSchema = Yup.object().shape({
        name: Yup.string()
          .required(() => translate('ROLE_NAME_IS_REQUIRED_FIELD'))
          .label('Name'),
        description: Yup.string()
          .required(() => translate('DESCRIPTION_IS_REQUIRED_FIELD'))
          .label('Short Description'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        values.permissions = selectedPermissions.value;

        await store.dispatch(Actions.UPDATE_ROLE, {
          id: role.value?.id,
          data: values,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_ROLE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'roles-listing' });
          });
        }
      };

      const updatePermissionsList = (list) => {
        selectedPermissions.value = list;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        groups: computed(() => store.getters.rolesGroupsList),
        updatePermissionsList,
        onSubmitUpdate,
        submitButton,
        translate,
        roleSchema,
        goBack,
        role,
        can,
      };
    },
  });
